import React, { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import {
    NavigationMenu,
    NavigationMenuList,
    NavigationMenuItem,
    NavigationMenuLink,
} from "../ui/navigation-menu"; // Ensure this path is correct for your project

export default function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [visible, setVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    // Prevent scroll when mobile menu open
    useEffect(() => {
        if (mobileOpen) {
            const scrollY = window.scrollY;
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = "100%";
        } else {
            // Restore scroll
            const scrollY = document.body.style.top;
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            window.scrollTo(0, parseInt(scrollY || "0") * -1);
        }
    }, [mobileOpen]);

    // Handle Scroll Logic
    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY;

            // Check if user has scrolled down from top (for background styling)
            setIsScrolled(currentY > 20);

            // Determine visibility (Hide on scroll down, show on scroll up)
            if (currentY > lastScrollY && currentY > 100) {
                setVisible(false);
            } else {
                setVisible(true);
            }

            setLastScrollY(currentY);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [lastScrollY]);

    return (
        <header className="w-full relative z-50">
            <nav
                className={`
                    fixed left-1/2 -translate-x-1/2
                    transition-all duration-500 ease-in-out
                    ${visible ? "top-6 opacity-100" : "-top-24 opacity-0"}
                    ${
                    isScrolled
                        /* 
                           FIX 1: Adjusted width percentages. 
                           Increased md:w to 90% and lg:w to 85% to give more room 
                           before shrinking to 75% on very large screens (xl).
                        */
                        ? "w-[95%] md:w-[90%] lg:w-[85%] xl:w-[75%] py-3 px-6 rounded-full bg-white/70 dark:bg-secondary/25 backdrop-blur-xl border border-black/5 dark:border-white/10 shadow-lg shadow-black/5"
                        : "w-full max-w-[93rem] py-6 px-6 bg-transparent border-transparent"
                }
                    flex items-center justify-between
                `}
            >
                {/* Logo Section */}
                <a href="/" className="flex-shrink-0 cursor-pointer group">
                    <div className="h-10 w-auto relative overflow-hidden">
                        <img
                            src="/assets/logo/FS_logo_Light.webp"
                            alt="Logo"
                            className="h-full w-auto object-contain dark:hidden transition-transform duration-300 group-hover:scale-105"
                        />
                        <img
                            src="/assets/logo/FS_logo_Dark.webp"
                            alt="Logo Dark"
                            className="h-full w-auto object-contain hidden dark:block transition-transform duration-300 group-hover:scale-105"
                        />
                    </div>
                </a>

                {/* Desktop Menu */}
                {/* FIX 2: Changed 'md:block' to 'lg:block'.
                    This keeps the Desktop menu hidden on iPad Portrait (approx 834px)
                    and only shows it on screens larger than 1024px. */}
                <div className="hidden lg:block">
                    <NavigationMenu>
                        <NavigationMenuList className="flex items-center gap-1">
                            <NavItem href="/" label="Home" />
                            <NavItem href="/#about" label="About" />
                            <NavItem href="/#services" label="Services" />
                            <NavItem href="/#mentorship" label="Mentorship" />
                            <NavItem href="/#team" label="Team" />

                            {/* CTA Button as the last item */}
                            <NavigationMenuItem className="ml-2">
                                <a
                                    href="/#contact"
                                    className="
                                        group relative px-5 py-2.5 rounded-full
                                        bg-dark text-light dark:bg-light dark:text-dark
                                        font-medium text-sm transition-all duration-300
                                        hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5
                                        flex items-center gap-2
                                    "
                                >
                                    <span>Contact</span>
                                    <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                                </a>
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>

                {/* Mobile Hamburger */}
                {/* FIX 3: Changed 'md:hidden' to 'lg:hidden'.
                    This ensures the Hamburger menu stays visible on Tablets/iPads. */}
                <button
                    className="lg:hidden p-2 text-dark dark:text-light hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors"
                    onClick={() => setMobileOpen(true)}
                    aria-label="Open Menu"
                >
                    <Menu size={24} strokeWidth={2} />
                </button>
            </nav>

            {/* Mobile Menu Overlay */}
            <div
                className={`
                    fixed inset-0 z-[60] bg-white/95 dark:bg-black/95 backdrop-blur-2xl
                    transition-all duration-500 ease-[cubic-bezier(0.32,0.725,0.25,1)]
                    flex flex-col justify-center items-center
                    ${mobileOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none"}
                `}
            >
                <button
                    className="absolute top-8 right-8 p-2 text-dark dark:text-light hover:rotate-90 transition-transform duration-300"
                    onClick={() => setMobileOpen(false)}
                >
                    <X size={32} strokeWidth={1.5} />
                </button>

                <ul className="flex flex-col items-center gap-8 text-center">
                    <MobileItem href="/" label="Home" delay="100ms" setMobileOpen={setMobileOpen} />
                    <MobileItem href="#about" label="About" delay="150ms" setMobileOpen={setMobileOpen} />
                    <MobileItem href="#services" label="Services" delay="200ms" setMobileOpen={setMobileOpen} />
                    <MobileItem href="/#mentorship" label="Mentorship" delay="250ms" setMobileOpen={setMobileOpen} />
                    <MobileItem href="#team" label="Team" delay="300ms" setMobileOpen={setMobileOpen} />
                    <MobileItem href="#contact" label="Contact Us" delay="350ms" isCTA setMobileOpen={setMobileOpen} />
                </ul>
            </div>
        </header>
    );
}

/* ------------------------------------------- */
/* Sub-Components                              */
/* ------------------------------------------- */

function NavItem({ href, label }) {
    return (
        <NavigationMenuItem>
            <NavigationMenuLink
                href={href}
                className="
                    relative px-4 py-2 text-sm font-medium text-dark/70 dark:text-light/70
                    hover:text-dark dark:hover:text-light transition-colors duration-200
                    rounded-full hover:bg-black/5 dark:hover:bg-white/10
                "
            >
                {label}
            </NavigationMenuLink>
        </NavigationMenuItem>
    );
}

/* ------------------------------------------- */
/* Mobile Item (Fixed Visibility)              */
/* ------------------------------------------- */

function MobileItem({ href, label, delay, isCTA, setMobileOpen }) {
    return (
        <li
            className="opacity-0"
            style={{
                animation: `fadeInUp 0.5s ease-out forwards`,
                animationDelay: delay
            }}
        >
            <style>
                {`
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>

            <a
                href={href}
                onClick={() => setMobileOpen(false)}
                className={`
                    block text-3xl font-medium tracking-tight
                    transition-all duration-300
                    ${isCTA
                    ? "text-primary dark:text-secondary font-bold mt-4"
                    : "text-dark dark:text-light hover:text-dark/50 dark:hover:text-light/50"
                }
                `}
            >
                {label}
            </a>
        </li>
    );
}